import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { useSearchedOptions } from '@/hooks/useSearchedOptions'
import { getApi } from '@/stores/api'
import { CREDIT_INDEX_SCORE_RANGES } from '@repo/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const creditIndexScoresSearchSchema = z.object({
  year: z.number().optional(),
  historyRange: z.enum(CREDIT_INDEX_SCORE_RANGES).optional(),
  keyword: z.string().optional(),
  legalName: z.array(z.string()).optional(),
  equifaxCode: z.array(z.string()).optional(),
  scoreTier: z.array(z.string()).optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export type CreditIndexScoresSearch = z.infer<typeof creditIndexScoresSearchSchema>

type CreditIndexScoresQuery = CreditIndexScoresSearch & { year: number }

async function getCreditIndexScores(queries: CreditIndexScoresQuery, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['credit-index-scores'].$get(
    { query: { ...stringifyValues(queries), year: String(queries.year) } },
    { init: { signal } },
  )

  return await res.json()
}

export type CreditIndexScoreRow = Awaited<
  ReturnType<typeof getCreditIndexScores>
>['results'][number]

export function useCreditIndexScores(queries: CreditIndexScoresQuery) {
  return useQuery({
    queryKey: ['creditIndexScores', queries],
    queryFn: ({ signal }) => getCreditIndexScores(queries, signal),
  })
}

async function getCreditIndexScoreYears(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['credit-index-scores'].years.$get({}, { init: { signal } })

  const { years } = await res.json()
  return years
}

export function useCreditIndexScoreYears() {
  return useQuery({
    queryKey: ['creditIndexScoreYears'],
    queryFn: ({ signal }) => getCreditIndexScoreYears(signal),
  })
}

async function getCreditIndexScoresLastImport(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['credit-index-scores']['last-import'].$get({}, { init: { signal } })

  const { lastImport } = await res.json()
  return lastImport
}

export function useCreditIndexScoresLastImport() {
  return useQuery({
    queryKey: ['creditIndexScoresLastImport'],
    queryFn: ({ signal }) => getCreditIndexScoresLastImport(signal),
  })
}

async function getCreditIndexScoreImports(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['credit-index-scores'].imports.$get({}, { init: { signal } })

  const { imports } = await res.json()
  return imports
}

export type CreditIndexScoreImport = Awaited<ReturnType<typeof getCreditIndexScoreImports>>[number]

export function useCreditIndexScoreImports() {
  return useQuery({
    queryKey: ['creditIndexScoreImports'],
    queryFn: ({ signal }) => getCreditIndexScoreImports(signal),
  })
}

export function useDeleteCreditIndexScoreImport() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (id: number) => {
      const api = getApi()
      const res = await api.credit['credit-index-scores'].imports[':id'].$delete({
        param: { id: String(id) },
      })
      return await res.json()
    },
    onSuccess: () => {
      // The deleted import's rows are gone; refresh every credit-index-scores view.
      for (const key of [
        'creditIndexScoreImports',
        'creditIndexScores',
        'creditIndexScoreYears',
        'creditIndexScoreLegalNames',
        'creditIndexScoresLastImport',
        'creditIndexScoreYearHistory',
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(t('creditIndexScores.importHistory.deleteSuccess'))
    },
    onError: () => {
      toast.error(t('creditIndexScores.importHistory.deleteError'))
    },
  })
}

async function getCreditIndexScoreLegalNames(
  year: number | undefined,
  search: string,
  offset: number,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['credit-index-scores']['legal-names'].$get(
    { query: stringifyValues({ year, search, offset }) },
    { init: { signal } },
  )

  const { legalNames, nextOffset } = await res.json()
  return { options: legalNames.map((name) => ({ value: name, label: name })), nextOffset }
}

/** Legal name options for the list's filter, searched as the user types; `selected` always offered. */
export function useCreditIndexScoreLegalNameOptions(selected: string[], year?: number) {
  return useSearchedOptions({
    queryKey: 'creditIndexScoreLegalNames',
    scope: year,
    selected,
    fetchMatches: (search, offset, signal) =>
      getCreditIndexScoreLegalNames(year, search, offset, signal),
  })
}

async function getCreditIndexScoreEquifaxCodes(
  search: string,
  offset: number,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['credit-index-scores']['equifax-codes'].$get(
    { query: stringifyValues({ search, offset }) },
    { init: { signal } },
  )

  const { equifaxCodes, nextOffset } = await res.json()
  return { options: equifaxCodes.map((code) => ({ value: code, label: code })), nextOffset }
}

/** Equifax code options for the list's filter, searched as the user types; `selected` always offered. */
export function useCreditIndexScoreEquifaxCodeOptions(selected: string[]) {
  return useSearchedOptions({
    queryKey: 'creditIndexScoreEquifaxCodes',
    selected,
    fetchMatches: getCreditIndexScoreEquifaxCodes,
  })
}

type CreditIndexScoreYearHistoryParams = {
  sapErp: string
  // The Score Trend window may span two calendar years, so history is fetched per-window.
  years: number[]
}

async function getCreditIndexScoreYearHistory(
  params: CreditIndexScoreYearHistoryParams,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['credit-index-scores']['year-history'].$get(
    {
      query: {
        sapErp: params.sapErp,
        years: JSON.stringify(params.years),
      },
    },
    { init: { signal } },
  )

  const { history } = await res.json()
  return history
}

export type CreditIndexScoreHistoryItem = Awaited<
  ReturnType<typeof getCreditIndexScoreYearHistory>
>[number]

export function useCreditIndexScoreYearHistory(params: CreditIndexScoreYearHistoryParams | null) {
  return useQuery({
    queryKey: ['creditIndexScoreYearHistory', params],
    queryFn: ({ signal }) => getCreditIndexScoreYearHistory(params!, signal),
    enabled: params !== null,
  })
}

async function getEquifaxCodeHistory(sapErp: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['credit-index-scores']['equifax-history'].$get(
    { query: { sapErp } },
    { init: { signal } },
  )

  // `isTruncated` ⇒ the backend capped the list; the dialog says so rather than implying the newest
  // entries are the whole history.
  const { history, isTruncated } = await res.json()
  return { history, isTruncated }
}

export type EquifaxCodeHistoryItem = Awaited<
  ReturnType<typeof getEquifaxCodeHistory>
>['history'][number]

/** Every Equifax number a customer has carried, newest first. Null `sapErp` ⇒ the dialog is shut. */
export function useEquifaxCodeHistory(sapErp: string | null) {
  return useQuery({
    queryKey: ['equifaxCodeHistory', sapErp],
    queryFn: ({ signal }) => getEquifaxCodeHistory(sapErp!, signal),
    enabled: sapErp !== null,
  })
}

type CreditIndexScoresExportFilters = {
  year: number
  keyword?: string
  legalName?: string[]
  equifaxCode?: string[]
  scoreTier?: string[]
}

async function getCreditIndexScoresCsv(
  filters: CreditIndexScoresExportFilters,
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(filters)
  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  return fetchCsvExport(`/credit/credit-index-scores/export?${queryParams}`, signal)
}

export function creditIndexScoresExportQueryOptions(filters: CreditIndexScoresExportFilters) {
  return {
    queryKey: ['creditIndexScoresExport', filters],
    queryFn: ({ signal }: { signal: AbortSignal }) => getCreditIndexScoresCsv(filters, signal),
  }
}
